import { getColorsMask, getLabelColorsMask } from '@shelflix/schemas/_common/validation';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Button } from '@shelflix/ui-web/components/ui/button';
import Icon from '@shelflix/ui-web/components/Icon';
import { useEffect, useRef, useState } from 'react';
import { GenericSelect } from './GenericSelect';

// Regex simples para hex tipo #FFF ou #FFFFFF
const isValidHex = (value: string) => /^#([0-9A-F]{3}){1,2}$/i.test(value);

type BaseColorInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  showSecondary?: boolean;
};

type PickerModeProps = BaseColorInputProps & {
  mode: 'picker';
};

type SelectModeProps = BaseColorInputProps & {
  mode: 'select';
  options: string[];
};

export type ColorInputProps = PickerModeProps | SelectModeProps;

export function ColorInput(props: ColorInputProps) {
  const { value, onChange, showSecondary = false, disabled = false } = props;

  const [internalValue, setInternalValue] = useState(value ?? '');
  const [isValid, setIsValid] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      const valid = isValidHex(value);
      setIsValid(valid);

      setInternalValue(value);
    }
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInternalValue(val);
    const valid = isValidHex(val);
    setIsValid(valid);
    onChange?.(val);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInternalValue(val);
    setIsValid(true);
    onChange?.(val);
  };
  const hexaRegex = /[0-9a-fA-F]/;
  const mask = ['#', hexaRegex, hexaRegex, hexaRegex, hexaRegex, hexaRegex, hexaRegex];

  if (props.mode === 'select') {
    const { options } = props; // agora TypeScript sabe que options existe

    return (
      <GenericSelect<{ label: string; value: string }>
        value={options.includes(internalValue) ? { label: internalValue, value: internalValue } : null}
        onSelect={(item) => {
          const val = item.value;
          setInternalValue(val);
          onChange?.(val);
        }}
        fetchItems={async () => options.map((i) => ({ label: getColorsMask(i), value: i }))}
        getItemLabel={(item) => item.label}
        getItemKey={(item) => item.value}
        renderItem={(item) => (
          <div className="flex items-center gap-2">
            <div
              className="h-6 w-6 rounded border"
              style={{
                backgroundColor: item.label,
                borderColor: '#8C8787',
              }}
            />
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{getLabelColorsMask(item.value)}</span>
            </div>
          </div>
        )}
        renderSelectedItem={(item) => (
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded border"
              style={{ backgroundColor: getColorsMask(item.label), borderColor: '#8C8787' }}
            />
            <span className="text-sm font-medium">{getLabelColorsMask(item.value)}</span>
          </div>
        )}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="relative flex w-full items-center gap-4">
      <Input
        type="text"
        className={`w-full p-2 ${isValid ? 'border-gray-300' : 'border-red-500'}`}
        placeholder="Digite o código hexadecimal"
        value={internalValue}
        onInput={handleTextChange}
        disabled={disabled}
        mask={mask}
      />
      {!showSecondary ? (
        <div className="relative h-9 w-9">
          {/* Color picker button with icon */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="btn-theme-primary h-9 w-9"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            <Icon name="palette" size={20} />
          </Button>
          <input
            type="color"
            value={isValid && internalValue ? internalValue : '#000000'}
            onChange={handleColorChange}
            ref={inputRef}
            className="absolute top-0 left-0 h-0 w-0 cursor-pointer opacity-0"
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}
